import { useAtom, useAtomValue } from "jotai";
import { hcostInboundAtom } from "@/store/hcost-atom";
import { CheckBox, DateBox, Lookup } from "devextreme-react";
import { getEmployeeSearchDataSource } from "@service/user/business-user-service";
import { UserConst } from "@constants/user-const";
import { useMemo, useRef, useState, useCallback } from "react";
import { CommonConst } from "@constants/common-const";
import { userAtom } from "@/store/global";
import { getCompanySearchDataSource } from "@service/company/company-service";
import ConSourceCell from "@feature/counsel/counsel-result-grid/conSource-cell";
import { DropDownOptions } from "devextreme-react/lookup";
import useCounselHook from "@feature/counsel/useCounselHook";
interface Props {
  accessType: string;
  isBrokerage?: boolean;
}

const CounselCompany = ({ accessType, isBrokerage }: Props) => {
  const hCostResult = useAtomValue(hcostInboundAtom);
  const [userInfo] = useAtom(userAtom);
  const [initCurrentUserFlag, setInitCurrentUserFlag] = useState(false);
  const counselorLookupRef = useRef<Lookup>(null);

  const { onValueChangeCounselHandler } = useCounselHook();

  const counselorDataSource = useMemo(() => {
    return getEmployeeSearchDataSource(
      accessType === CommonConst.ACCESS_TYPE_ADMIN ? CommonConst.ACCESS_TYPE_COMPANY : CommonConst.ACCESS_TYPE_MY,
      hCostResult.company?.id,
      UserConst.accountStatus.NORMAL,
      UserConst.workStatus.EMPLOYMENT,
    );
  }, [accessType, hCostResult.company?.id]);

  const lookupCounselorDisplayExpr = (data: any) => {
    return data ? `${data.name}(${data.username})` : "";
  };

  const onLookupOpened = useCallback(
    (e: any) => {
      if (accessType !== CommonConst.ACCESS_TYPE_ADMIN) {
        if (!hCostResult.counselor && !initCurrentUserFlag) {
          e.component._searchBox.option("value", userInfo.name);
          setInitCurrentUserFlag(true);
        }
      }
    },
    [accessType, hCostResult.counselor, initCurrentUserFlag, userInfo.name],
  );

  const counselor = useMemo(() => {
    return (
      <Lookup
        dataSource={counselorDataSource}
        displayExpr={lookupCounselorDisplayExpr}
        ref={counselorLookupRef}
        showClearButton={true}
        value={hCostResult.counselor}
        clearButtonText="담당 제거"
        searchExpr={["name", "username"]}
        searchPlaceholder="이름, 아이디로 검색"
        onValueChanged={onValueChangeCounselHandler((draft, value) => {
          draft.counselor = value;
        })}
        onOpened={onLookupOpened}
      >
        <DropDownOptions showTitle={false} height={"20rem"} />
      </Lookup>
    );
  }, [counselorDataSource, hCostResult.counselor, onValueChangeCounselHandler, onLookupOpened]);

  const lookupCompanyDisplayExpr = (data: any) => {
    let companyName = "";
    let ceoName = "";
    let address = "";
    !!data?.companyName ? (companyName = `${data.companyName}`) : "";
    !!data?.ceoName ? (ceoName = `/${data.ceoName}`) : "";
    !!data?.address ? (address = `/${data.address.district_1} ${data.address.district_2}`) : "";
    return data ? `${companyName}(${data.id})${ceoName}${address}` : "";
  };

  const companySearchDataSource = useMemo(() => {
    return getCompanySearchDataSource();
  }, []);

  const companyName = useMemo(
    () => (
      <Lookup
        dataSource={companySearchDataSource}
        displayExpr={lookupCompanyDisplayExpr}
        minSearchLength={1}
        searchExpr={["id", "companyName", "ceoName"]}
        showClearButton={false}
        value={hCostResult.company}
        readOnly={isBrokerage}
        searchPlaceholder="대표 상호명 및 대표자로 검색"
        onValueChanged={onValueChangeCounselHandler(async (draft, value) => {
          draft.company = value;
          counselorLookupRef.current.instance.reset();
          draft.counselor = value.defaultEmployee;
        })}
      />
    ),
    [companySearchDataSource, onValueChangeCounselHandler, hCostResult.company],
  );

  return (
    <div className="content-fieldset">
      <h3 className="content-fieldset-title">상담 담당</h3>
      <div className="content-fieldset-title-desc" />

      {accessType === CommonConst.ACCESS_TYPE_ADMIN && (
        <>
          <div className="content-field row ">
            <div className="content-field-label">
              <div className="content-label-name">담당 조직</div>
              <div className="content-label-desc" />
            </div>
            <div className="content-field-value full">
              {companyName}
              <div className="content-value-desc" />
            </div>
          </div>
        </>
      )}

      <div className="content-field row ">
        <div className="content-field-label">
          <div className="content-label-name">담당자</div>
          <div className="content-label-desc" />
        </div>
        <div className="content-field-value full">
          {counselor}
          <div className="content-value-desc" />
        </div>
      </div>

      {!isBrokerage && (
        <div className="content-field row ">
          <div className="content-field-label">
            <div className="content-label-name">상담 출처</div>
            <div className="content-label-desc" />
          </div>
          <div className="content-field-value full flex flex-wrap">
            <ConSourceCell data={{ data: hCostResult }} />

            <div className="content-value-desc" />
          </div>
        </div>
      )}

      {!isBrokerage && accessType === CommonConst.ACCESS_TYPE_ADMIN && (
        <>
          <div className="content-field row">
            <div className="content-field-label">
              <div className="content-label-name">중개 여부</div>
              <div className="content-label-desc" />
            </div>
            <div className="content-field-value full">
              <CheckBox value={hCostResult.conBrokerage} readOnly={true} />
              <div className="content-value-desc" />
            </div>
          </div>
          <div className="content-field row">
            <div className="content-field-label">
              <div className="content-label-name">중개 일시</div>
              <div className="content-label-desc" />
            </div>
            <div className="content-field-value full">
              <DateBox
                calendarOptions={{ maxZoomLevel: "month" }}
                showClearButton={true}
                value={hCostResult.conBrokerageDate}
                displayFormat="yyyy년 MM월 dd일"
                dateSerializationFormat="yyyy-MM-dd"
                readOnly={true}
              />
              <div className="content-value-desc" />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CounselCompany;
